import React, { useState, useEffect } from 'react';
import axios from 'axios';
import baseURL from './baseURl'; // Ensure you have the correct baseURL
import logo from './logo.new1.png';

const Speed = () => {
  const [formData, setFormData] = useState({
    clientName: '',
    langdiWeight: [''], // Initialize as an array
    touch: [''], // Initialize as an array
    purity: '',
    pureWeightGiven: '',
    rate: [''],
    unit: 'g', // Default unit set to kilograms
  });

  const [responseMessage, setResponseMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [calculatedPureWeight, setCalculatedPureWeight] = useState(0); // Set calculatedPureWeight to default 0
  const [showModal, setShowModal] = useState(false); // State to manage modal visibility
  const [receiptData, setReceiptData] = useState({}); // State to store receipt data
  const [clients, setClients] = useState([]); // State to store all clients
  const [filteredClients, setFilteredClients] = useState([]); // State to store filtered clients
  const [searchTerm, setSearchTerm] = useState(''); // State for search term
  const [showDropdown, setShowDropdown] = useState(false); // State to control dropdown visibility
  const [cashAmountInStock, setCashAmountInStock] = useState(0);
  const [excessAmount, setExcessAmount] = useState(0);
  const [selectedClient, setSelectedClient] = useState('');
  const [calculatedTotal, setCalculatedTotal] = useState(0);
  const [originalTotal, setOriginalTotal] = useState(0);
  const [amountPending, setAmountPending] = useState(0);
  const [advance, setAdvance] = useState(null);
  const [useAdvance, setUseAdvance] = useState(false);
  const [individualPureWeights, setIndividualPureWeights] = useState([]); // Add a new state for individual pure weights
  const [calculatedPureWeights, setCalculatedPureWeights] = useState([]); // New state to store pure weight for each entry

  // Fetch clients from the API when the component mounts
  useEffect(() => {
    const fetchClients = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/clients`);
        console.log('Fetched Clients:', response.data); // Log the fetched clients
        setClients(response.data);
        setFilteredClients(response.data);
      } catch (error) {
        console.error('Error fetching clients:', error);
      }
    };

    fetchClients();
  }, []);

  // Effect to calculate pure weight whenever langdiWeight, touch, and purity change
  const calculatePureWeight = () => {
    // Check if all input arrays are empty or contain only empty strings
    const isEmptyInput = 
      formData.langdiWeight.every(w => w === '' || w === 0) ||
      formData.touch.every(t => t === '' || t === 0);

    if (isEmptyInput) {
      return {
        totalPureWeight: 0,
        individualPureWeights: [],
        calculatedPureWeights: []
      };
    }

    let totalPureWeight = 0;
    const individualPureWeights = [];
    const calculatedPureWeights = [];

    formData.langdiWeight.forEach((weight, index) => {
      const weightValue = parseFloat(weight) || 0;
      const touchValue = parseFloat(formData.touch[index]) || 0;
      const purityValue = formData.purity || 100; // Default to 99.50 if not specified
      
      // Precise calculation with manual truncation
      const pureWeight = Math.floor(((weightValue * touchValue) / purityValue) * 1000) / 1000;
      
      totalPureWeight += pureWeight;
      individualPureWeights.push(pureWeight);
      calculatedPureWeights.push(pureWeight);
    });

    // Ensure total is truncated to 3 decimal places
    const formattedTotalPureWeight = Math.floor(totalPureWeight * 1000) / 1000;

    return {
      totalPureWeight: formattedTotalPureWeight,
      individualPureWeights,
      calculatedPureWeights
    };
  };

  useEffect(() => {
    const { totalPureWeight, individualPureWeights, calculatedPureWeights } = calculatePureWeight();
    setCalculatedPureWeight(totalPureWeight);
    setIndividualPureWeights(individualPureWeights); // Update individualPureWeights state
    setCalculatedPureWeights(calculatedPureWeights); // Update calculatedPureWeights state
  }, [formData.langdiWeight, formData.touch, formData.purity]);

  // Filter clients based on the search term whenever it changes
  useEffect(() => {
    // If no client name is set, show all clients
    if (!formData.clientName) {
      setFilteredClients(clients);
      return;
    }

    // Case-insensitive search across multiple fields
    const filtered = clients.filter(client => 
      client.name.toLowerCase().includes(formData.clientName.toLowerCase())
    );
    setFilteredClients(filtered);
  }, [formData.clientName, clients]);

  const handleSelectClient = async (selectedClient) => {
    // Comprehensive logging for client selection
    console.log('🎯 Client Selection - Full Process:', {
      selectedClient: selectedClient,
      searchTerm: searchTerm,
      formDataBefore: { ...formData }
    });

    // Determine client name with priority
    let clientName = '';
    if (selectedClient) {
      // Prioritize full name from selected client object
      clientName = selectedClient.name || 
                   selectedClient.clientName || 
                   (typeof selectedClient === 'string' ? selectedClient : '');
    }

    // Fallback to search term if no client name found
    if (!clientName && searchTerm) {
      clientName = searchTerm.trim();
    }

    // Validate client name
    if (!clientName) {
      console.error('❌ Unable to determine client name');
      setErrorMessage('Please select a valid client');
      return;
    }

    console.log('🔍 Selected Client Name:', clientName);

    // Update form data
    setFormData(prevData => {
      const updatedFormData = {
        ...prevData,
        clientName: clientName
      };
      
      console.log('📝 Updated Form Data:', {
        oldFormData: prevData,
        newFormData: updatedFormData
      });

      return updatedFormData;
    });

    // Reset UI states
    setSearchTerm(clientName);
    setFilteredClients([]);
    setShowDropdown(false);

    // Fetch client advance with the full client object
    await fetchClientAdvance(selectedClient);
  };

  const fetchClientAdvance = async (client) => {
    // Comprehensive logging for client name extraction
    console.log('🔍 Advance Fetch - Client Details:', {
      inputClient: client,
      formDataClientName: formData.clientName,
      searchTerm: searchTerm
    });

    // Multiple strategies to extract client name
    let clientName = '';
    
    // 1. Try client object name properties
    if (client) {
      clientName = client.name || 
                   client.clientName || 
                   (typeof client === 'string' ? client : '');
    }
    
    // 2. Fallback to formData clientName
    if (!clientName) {
      clientName = formData.clientName;
    }
    
    // 3. Final fallback to search term
    if (!clientName) {
      clientName = searchTerm;
    }

    // Validate client name
    if (!clientName || clientName.trim() === '') {
      console.error('❌ Unable to determine client name for advance fetch');
      setAdvance(null);
      setErrorMessage('Unable to fetch advance: No client name found');
      return;
    }

    try {
      console.log(`🚀 Attempting to fetch advance for client: ${clientName}`);
      
      // Ensure full client name is used and properly encoded
      const response = await axios.get(`${baseURL}/api/transactions/advance/pureweight/${encodeURIComponent(clientName.trim())}`);
      
      // Extensive logging of the entire response
      console.log('📦 Full Advance Response:', {
        status: response.status,
        data: response.data
      });

      // Extract advance data from the new response structure
      const advanceData = response.data?.advance;
      
      // Validate and format advance data
      if (advanceData && advanceData.pureWeight !== undefined) {
        const formattedAdvance = {
          pureWeight: Number(advanceData.pureWeight),
          type: advanceData.type || 'given'
        };

        console.log('✅ Formatted Advance:', formattedAdvance);
        setAdvance(formattedAdvance);
      } else {
        console.warn(`⚠️ No valid advance data received for client: ${clientName}`);
        console.warn('Raw Response Data:', response.data);
        setAdvance(null);
        setErrorMessage(`No advance data found for ${clientName}`);
      }
    } catch (error) {
      console.error(`❌ Complete error fetching advance for client: ${clientName}`, {
        name: error.name,
        message: error.message
      });
      
      // Detailed error logging
      console.log('🔴 Detailed Error:', {
        response: error.response ? {
          data: error.response.data,
          status: error.response.status
        } : 'No response'
      });
      
      // Set error message for user
      setErrorMessage(`Failed to fetch advance for ${clientName}. Please check the client name.`);
      setAdvance(null);
    }
  };

  // Calculate total amount with precise handling
  const calculateTotals = () => {
    let total = 0;
    let originalTotal = 0;

    formData.langdiWeight.forEach((weight, index) => {
      const weightValue = parseFloat(weight) || 0;
      const rate = parseFloat(formData.rate[index]) || 0;
      const touchValue = parseFloat(formData.touch[index]) || 0;
      const purityValue = formData.purity || 99.50;

      // Precise pure weight calculation
      const pureWeight = Math.floor(((weightValue * touchValue) / purityValue) * 1000) / 1000;
      
      // Calculate total amount
      total += pureWeight * rate;
      originalTotal += weightValue * rate;
    });

    // Precise balance weight calculation
    const calculatedPureWeight = calculatePureWeight().totalPureWeight;
    const pureWeightGiven = parseFloat(formData.pureWeightGiven) || 0;
    const balanceWeight = Math.floor((calculatedPureWeight - pureWeightGiven) * 1000) / 1000;

    return {
      total: Math.floor(total),
      originalTotal: Math.floor(originalTotal),
      calculatedPureWeight,
      balanceWeight,
      amountToBePaid: Math.floor(balanceWeight * (formData.rate[0] || 0)),
      advanceApplied: 0
    };
  };

  const calculateTotalAmount = () => {
    let totalAmount = 0;
    let totalWeight = 0;

    // Calculate total weight and amount from rate entries
    formData.rate.forEach((rate, index) => {
      const weight = formData.langdiWeight[index] || 0;
      const rateValue = rate || 0;
      totalWeight += weight;
      totalAmount += weight * rateValue;
    });

    // Adjust for pure weight given
    const adjustedWeight = totalWeight - formData.pureWeightGiven;

    return {
      totalWeight: adjustedWeight,
      totalAmount: totalAmount
    };
  };

  // Helper function to format number with intelligent trimming
  const formatNumber = (num, decimalPlaces = 3) => {
    // Convert to number and round to specified decimal places
    const rounded = Number(Number(num).toFixed(decimalPlaces));
    
    // Convert to string to manipulate
    let formattedStr = rounded.toString();
    
    // Remove trailing zeros after the decimal point
    if (formattedStr.includes('.')) {
      formattedStr = formattedStr.replace(/\.?0+$/, '');
    }
    
    return formattedStr;
  };

  const calculateTotalDetails = () => {
    // Fallback to 0 if calculatedPureWeight is not initialized
    const currentCalculatedPureWeight = calculatedPureWeight || 0;

    console.log('calculatedPureWeight:', currentCalculatedPureWeight);
    console.log('formData.pureWeightGiven:', formData.pureWeightGiven);
    console.log('advance:', advance);

    const pureWeightGiven = parseFloat(formData.pureWeightGiven || 0);
    const advancePureWeight = parseFloat(advance?.pureWeight || 0);

    console.log('Parsed calculatedPureWeight:', currentCalculatedPureWeight);
    console.log('Parsed pureWeightGiven:', pureWeightGiven);
    console.log('Parsed advancePureWeight:', advancePureWeight);

    let pendingWeight = null;
    
    if (useAdvance && advance) {
      let remainingWeight = currentCalculatedPureWeight - advancePureWeight;
      
      if (pureWeightGiven > 0) {
        remainingWeight = remainingWeight - pureWeightGiven;
      }
      
      pendingWeight = Number(remainingWeight.toFixed(3));
    } else {
      // Use precise subtraction and ensure 3 decimal places
      pendingWeight = Number((currentCalculatedPureWeight - pureWeightGiven).toFixed(3));
    }

    console.log('Final pendingWeight:', pendingWeight);

    return {
      pendingWeight: pendingWeight,
    };
  };

  const handleArrayChange = (index, field, value) => {
    const updatedArray = [...formData[field]];
    updatedArray[index] = value;
    setFormData({ ...formData, [field]: updatedArray });
  };
  
  const addInputField = (field) => {
    setFormData({ ...formData, [field]: [...formData[field], ""] });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'clientName') {
      // Reset client-related states when client name changes
      setSelectedClient('');
      setFilteredClients([]);
      setShowDropdown(true);
      setAdvance(null);
      setUseAdvance(false);
    }

    setFormData({
      ...formData,
      [name]: value
    });

    if (name === 'clientName') {
      // Filter clients based on input
      const filtered = clients.filter(client => 
        client.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredClients(filtered);
    }
  };

  const addEntryField = () => {
    setFormData({
      ...formData,
      langdiWeight: [...formData.langdiWeight, ''],
      touch: [...formData.touch, ''],
      rate: [...formData.rate, ''],
    });
  };
  const removeEntryField = (index) => {
    setFormData({
      ...formData,
      langdiWeight: formData.langdiWeight.filter((_, i) => i !== index),
      touch: formData.touch.filter((_, i) => i !== index),
      rate: formData.rate.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setResponseMessage('');

    // Validate client name
    if (!formData.clientName) {
      setErrorMessage('Please select a client');
      return;
    }

    // Ensure calculatedPureWeight has a valid value
    const safeCalculatedPureWeight = calculatedPureWeight || 0;

    try {
      // Log all form data details before preparing transaction data
      console.log('=== FULL FORM DATA SUBMISSION ===');
      console.log('Form Data:', JSON.stringify(formData, null, 2));
      console.log('Use Advance:', useAdvance);
      console.log('Advance Details:', JSON.stringify(advance, null, 2));
      console.log('Calculated Pure Weight:', safeCalculatedPureWeight);
      console.log('Individual Pure Weights:', individualPureWeights);

      // Prepare transaction data with calculated pure weights
      const transactionData = {
        ...formData,
        pureWeightCalculated: calculatedPureWeight,
        calculatedPureWeights: calculatedPureWeights, // Add this line
        totalPureWeight: calculatedPureWeight,
        // ... other existing fields
      };

      // Conditionally add advance details if useAdvance is true
      if (useAdvance && advance) {
        transactionData.useAdvance = true;
        transactionData.advanceType = advance.type;
        transactionData.advancePureWeight = parseFloat(advance.pureWeight || 0);
      }

      // Detailed logging of final payload
      console.log('Final Transaction Payload:', JSON.stringify(transactionData, null, 2));
      console.log('=== END FORM DATA SUBMISSION ===');

      const response = await axios.post(`${baseURL}/api/transactions/calculate`, transactionData);
      
      setReceiptData(response.data);
      setResponseMessage('Transaction created successfully!');
      setShowModal(true);
    } catch (error) {
      console.error('Transaction Creation Error:', error);
      setErrorMessage(error.response?.data?.message || 'Error creating transaction');
    }
  };

  const closeModal = () => setShowModal(false);
  const handlePrint = () => {
    const printWindow = document.createElement('iframe');
    printWindow.style.position = 'absolute';
    printWindow.style.width = '0';
    printWindow.style.height = '0';
    printWindow.style.border = 'none';
    
    document.body.appendChild(printWindow);
  
    const doc = printWindow.contentWindow.document;
    doc.open();
    doc.write('<html><head><title>Print Transaction</title>');
    doc.write('<style>');
    doc.write('body { font-family: Arial, sans-serif; line-height: 1.6; margin: 0; padding: 20px; background-color: #f9f9f9; }');
    doc.write('.container { width: 80%; margin: 0 auto; padding: 20px; background-color: #fff; border: 1px solid #ccc; border-radius: 8px; box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1); }');
    doc.write('h1 { text-align: center; margin: 10px 0; font-size: 20px; }');
    doc.write('.header { text-align: left; margin-bottom: 20px; }');
    doc.write('.logo { display: inline-block; width: 100px; float: left; }');
    doc.write('.date { display: inline-block; float: right; font-size: 14px; }');
    doc.write('.clear { clear: both; }');
    doc.write('table { width: 100%; margin-top: 20px; border-collapse: collapse; }');
    doc.write('th, td { border: 1px solid #000; padding: 8px; text-align: center; }');
    doc.write('th { background-color: #f2f2f2; }');
    doc.write('.footer { margin-top: 20px; border-top: 1px solid #000; padding-top: 10px; text-align: center; }');
    doc.write('</style>');
    doc.write('</head><body>');
    doc.write('<div class="container">');
    
    // Header Section
    doc.write('<h1>SHREE GANESHAY NAMH</h1>');
    doc.write('<div class="header">');
    doc.write('<p class="date">' + (receiptData.transaction.date ? formatDate(receiptData.transaction.date) : 'N/A') + '</p>');
    doc.write('<img src="' + logo + '" class="logo" alt="Logo" style="height: 50px; width: 50px;">');

    doc.write('</div>');
    doc.write('<div class="clear"></div>');
    
    // Transaction Details
    doc.write('<p><strong>Receipt No:</strong> ' + (receiptData.transaction.receiptNo || 'N/A') + '</p>');
    doc.write('<p><strong>Name:</strong> ' + (receiptData?.transaction.clientName || 'N/A') + '</p>');
    
    // Table Section
    doc.write('<table>');
    doc.write('<thead>');
    doc.write('<tr>');
    doc.write('<th>Langdi Weight</th><th>Touch</th><th>Pure Weight</th><th>Pure Weight Given</th>');
    doc.write('<th>Balance Weight</th><th>Rate</th><th>Total</th></tr>');
    doc.write('</thead>');
    
    doc.write('<tbody>');
    
    // Print each row of langdi weight and touch
    (receiptData.transaction.langdiWeight || []).forEach((weight, index) => {
      doc.write('<tr>');
      doc.write('<td>' + weight + '</td>');
      doc.write('<td>' + (receiptData.transaction.touch?.[index] || '0') + '</td>');
      
      // For the first row, write the merged cells
      if (index === 0) {
        doc.write('<td rowspan="' + (receiptData.transaction.langdiWeight || []).length + '">' + (receiptData.transaction.pureWeightCalculated || '0.000') + '</td>');
        doc.write('<td rowspan="' + (receiptData.transaction.langdiWeight || []).length + '">' + (receiptData.transaction.pureWeightGiven || '0.000') + '</td>');
        doc.write('<td rowspan="' + (receiptData.transaction.langdiWeight || []).length + '">' + (receiptData.transaction.balanceWeight ? receiptData.transaction.balanceWeight.toFixed(3) : '0.000') + '</td>');
        doc.write('<td rowspan="' + (receiptData.transaction.langdiWeight || []).length + '">' + (Array.isArray(receiptData.transaction.rate) ? receiptData.transaction.rate[0] : receiptData.transaction.rate || '0.000') + '</td>');
        doc.write('<td rowspan="' + (receiptData.transaction.langdiWeight || []).length + '">₹' + (receiptData.transaction.amountToBePaid || receiptData.transaction.amountToBeAdded || 'N/A') + '</td>');
      }
      
      doc.write('</tr>');
    });
    
    doc.write('</tbody>');
    doc.write('<tfoot>');
    doc.write('<tr>');
    doc.write('<td colspan="6" style="text-align: right;"><strong>Total Amount: ₹' + (receiptData.transaction.amountToBePaid || 'N/A') + '</strong></td>');
    doc.write('</tr>');
    doc.write('</tfoot>');
    doc.write('</table>');
    
    // Footer Section
    doc.write('<div class="footer">');
    doc.write('<p>Thank you for your Business!</p>');
    doc.write('<p>Do visit Again!</p>');
    doc.write('</div>');
    
    doc.write('</div>');
    doc.write('</body></html>');
    doc.close();

    // Wait for the iframe content to load before printing
    printWindow.onload = function() {
        printWindow.contentWindow.focus();
        printWindow.contentWindow.print();
        document.body.removeChild(printWindow); // Clean up the iframe
    };
};

// Helper function for date formatting
function formatDate(dateString) {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

  const resetForm = () => {
    setFormData({
      clientName: '',
      langdiWeight: [0],
      touch: [0],
      rate: [0],
      purity: 0,
      pureWeightGiven: 0,
      unit: 'g'
    });
    setSelectedClient('');
    setAdvance(null);
    setUseAdvance(false);
  };

  const renderAdvanceInfo = () => {
    return (
      <div className="col-12">
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="card-title">Advance Details</h5>
            <div className="form-check mb-2">
              <input
                type="checkbox"
                className="form-check-input"
                id="useAdvance"
                checked={useAdvance}
                onChange={(e) => {
                  const newUseAdvance = e.target.checked;
                  setUseAdvance(newUseAdvance);
                }}
              />
              <label className="form-check-label" htmlFor="useAdvance">Use Advance</label>
            </div>
            {advance && advance.pureWeight !== undefined && advance.pureWeight !== null ? (
              <div className="col-12 mt-2">
                <div className="alert alert-info mb-0">
                  <strong>Available Pure Weight Advance:</strong> {Number(advance.pureWeight).toFixed(3)} g
                  <br />
                  <small className="text-muted">
                    This pure weight will be subtracted from the total calculation
                  </small>
                </div>
              </div>
            ) : (
              <div className="col-12 mt-2">
                <div className="alert alert-warning mb-0">
                  No advance data available for this client
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Function to calculate pure weight for multiple entries
  const calculatePureWeightForArray = (langdiWeights, touches, purity) => {
    if (!Array.isArray(langdiWeights) || !Array.isArray(touches)) {
      return '0.000';
    }

    const pureWeights = langdiWeights.map((weight, index) => {
      const touch = touches[index] || 0;
      const pureWeight = (weight * touch * purity) / (24 * 100);
      return pureWeight.toFixed(3);
    });

    return pureWeights;
  };

  return (
    <div className="container mt-4">
      <h1 className="text-center mb-4">Transaction Speed</h1>

      <form onSubmit={handleSubmit} className="bg-light p-4 rounded shadow-sm">
        <div className="mb-3">
          <label htmlFor="clientName" className="form-label">Client Name</label>
          <input
            type="text"
            id="clientName"
            name="clientName"
            value={formData.clientName}
            onChange={handleChange}
            className="form-control form-control-sm"
          />
          {showDropdown && filteredClients.length > 0 && (
            <ul className="list-group mt-1">
              {filteredClients.map((client) => (
                <li
                  key={client.id || client.name} // Fallback to name if no id
                  className="list-group-item list-group-item-action"
                  onClick={() => {
                    console.log('Selected Client in List:', client);
                    handleSelectClient(client);
                  }}
                >
                  {client.name} {client.mobile ? `(${client.mobile})` : ''}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mb-3">
        <label className="form-label">Langdi Weight</label>
        {formData.langdiWeight.map((weight, index) => (
          <div key={index} className="d-flex align-items-center">
            <input
              type="number"
              name={`langdiWeight-${index}`}
              value={weight}
              onChange={(e) => handleArrayChange(index, 'langdiWeight', e.target.value)}
              className="form-control form-control-sm"
              required
            />
            <button type="button" onClick={() => addInputField('langdiWeight')} className="btn btn-sm btn-secondary ms-2">
              +
            </button>
          </div>
        ))}
      </div>

      <div className="mb-3">
        <label className="form-label">Touch</label>
        {formData.touch.map((touchValue, index) => (
          <div key={index} className="d-flex align-items-center">
            <input
              type="number"
              name={`touch-${index}`}
              value={touchValue}
              onChange={(e) => handleArrayChange(index, 'touch', e.target.value)}
              className="form-control form-control-sm"
              required
            />
            <button type="button" onClick={() => addInputField('touch')} className="btn btn-sm btn-secondary ms-2">
              +
            </button>
          </div>
        ))}
      </div>
      <div className="mb-3">
        <label htmlFor="purity" className="form-label">Purity (%)</label>
        <input
          type="number"
          id="purity"
          name="purity"
          value={formData.purity}
          onChange={(e) => setFormData({ ...formData, purity: parseFloat(e.target.value) })}
          className="form-control form-control-sm"
        />
      </div>
      <div className="mb-3">
        <label className="form-label">Unit</label>
        <div>
          <div className="form-check form-check-inline">
            <input
              type="radio"
              id="kg"
              name="unit"
              value="kg"
              checked={formData.unit === 'kg'}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className="form-check-input"
            />
            <label htmlFor="kg" className="form-check-label">Kg</label>
          </div>
          <div className="form-check form-check-inline">
            <input
              type="radio"
              id="g"
              name="unit"
              value="g"
              checked={formData.unit === 'g'}
              onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
              className="form-check-input"
            />
            <label htmlFor="g" className="form-check-label">Grams</label>
          </div>
        </div>
      </div>

      <div className="mb-3">
        <label htmlFor="calculatedPureWeight" className="form-label">Calculated Pure Weight</label>
        <input
          type="number"
          id="calculatedPureWeight"
          name="calculatedPureWeight"
          value={calculatedPureWeight}
          className="form-control form-control-sm"
          disabled
        />
      </div>
      <div className="mb-3">
        <label htmlFor="pureWeightGiven" className="form-label">Pure Weight Given</label>
        <input
          type="number"
          id="pureWeightGiven"
          name="pureWeightGiven"
          value={formData.pureWeightGiven}
          onChange={(e) => {
            const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
            setFormData({ ...formData, pureWeightGiven: value });
          }}
          className="form-control form-control-sm"
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Rate</label>
        {formData.rate.map((rateValue, index) => (
          <div key={index} className="d-flex align-items-center">
            <input
              type="number"
              name={`rate-${index}`}
              value={rateValue}
              onChange={(e) => handleArrayChange(index, 'rate', e.target.value)}
              className="form-control form-control-sm"
            />
            <button type="button" onClick={() => addInputField('rate')} className="btn btn-sm btn-secondary ms-2">
              +
            </button>
          </div>
        ))}
      </div>

      <div className="form-group mb-3">
        <div className="custom-control custom-checkbox">
          <input
            type="checkbox"
            className="custom-control-input"
            id="useAdvance"
            checked={useAdvance}
            onChange={(e) => {
              const newUseAdvance = e.target.checked;
              setUseAdvance(newUseAdvance);
            }}
            disabled={!advance}
          />
          <label className="custom-control-label" htmlFor="useAdvance">
            Use Advance {advance ? `(${advance.pureWeight.toFixed(3)} g)` : ''}
          </label>
        </div>
      </div>

      {/* Pending Weight Section */}
      {calculateTotalDetails().pendingWeight !== null && (
        <div className="row mb-3">
          <div className="col-md-4">
            <div className="card text-center">
              <div className="card-body">
                <h6 className="card-title">Pending Weight</h6>
                <p className="card-text display-4">
                  {calculateTotalDetails().pendingWeight} g
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

        <button type="submit" className="btn btn-primary">Submit</button>
        {/* Error Message Display */}
        {errorMessage && (
          <div className="alert alert-danger mt-2" role="alert">
            {errorMessage}
            <button 
              type="button" 
              className="close" 
              onClick={() => setErrorMessage(null)}
            >
              <span>&times;</span>
            </button>
          </div>
        )}
        {responseMessage && <div className="text-success mt-2">{responseMessage}</div>}
      </form>

      {/* Modal to show receipt data */}
  {/* Modal to show receipt data */}
  {showModal && (
  <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
    <div className="modal-dialog modal-lg">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Receipt Details</h5>
          <button type="button" className="close" onClick={closeModal}>
            <span>&times;</span>
          </button>
        </div>
        <div className="modal-body" id="receiptContent">
          <img src={logo} alt="Logo" className="img-fluid mb-3" style={{ maxHeight: '50px' }} />
          
          {/* Date Display */}
          <div className="d-flex justify-content-between">
            <h6 className="font-weight-bold mb-0">Date:</h6>
            <h6 className="font-weight-bold mb-0">
            {new Date(receiptData.transaction.date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) || 'N/A'}

            </h6>
          </div>
          <h6 className="font-weight-bold">Receipt No: {receiptData.transaction.receiptNo || 'N/A'}</h6>
          <h6 className="font-weight-bold">Client Name: {receiptData?.transaction.clientName || 'N/A'}</h6>

          {/* Details Table */}
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Langdi Weight</th><th>Touch</th><th>Pure Weight</th><th>Pure Weight Given</th>
                <th>Balance Weight</th><th>Rate</th>
                {/* <th>Total Amount</th> */}
              </tr>
            </thead>
            <tbody>
              {(receiptData.transaction.langdiWeight || []).map((weight, index) => {
                // Calculate pure weight for this entry
                const pureWeight = 
                  receiptData.transaction.pureWeightCalculated || 
                  (receiptData.transaction.calculatedPureWeights?.[index] || 
                   receiptData.transaction.individualPureWeights?.[index] || 
                   '0.000');

                // Calculate rate for this entry
                const rate = Array.isArray(receiptData.transaction.rate) 
                  ? receiptData.transaction.rate[index] 
                  : receiptData.transaction.rate;

                return (
                  <tr key={index}>
                    <td>{weight}</td>
                    <td>{receiptData.transaction.touch?.[index] || 'N/A'}</td>
                    {index === 0 && (
                      <td rowSpan={(receiptData.transaction.langdiWeight || []).length}>
                        {pureWeight}
                      </td>
                    )}
                    {index === 0 && (
                      <td rowSpan={(receiptData.transaction.langdiWeight || []).length}>
                        {receiptData.transaction.pureWeightGiven !== undefined 
                          ? Number(receiptData.transaction.pureWeightGiven).toFixed(3) 
                          : 'N/A'}
                      </td>
                    )}
                    {index === 0 && (
                      <td rowSpan={(receiptData.transaction.langdiWeight || []).length}>
                        {receiptData.transaction.balanceWeight !== undefined 
                          ? Number(receiptData.transaction.balanceWeight).toFixed(3) 
                          : 'N/A'}
                      </td>
                    )}
                    {index === 0 && (
                      <td rowSpan={(receiptData.transaction.langdiWeight || []).length}>
                        {rate !== undefined && rate !== null 
                          ? Number(rate).toFixed(0) 
                          : '0.000'}
                      </td>
                    )}
                  </tr>
                );
              })}
              {/* Display Total Amount in Final Row */}
              <tr>
                <td colSpan="6" className="text-right">
                  <strong>Total Amount: ₹{receiptData.transaction.amountToBePaid || receiptData.transaction.amountToBeAdded ||'N/A'}</strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={closeModal}>
            Close
          </button>
          <button type="button" className="btn btn-secondary" onClick={handlePrint}>
            Print
          </button>
        </div>
      </div>
    
    </div>
  </div>
)}






    </div>
  );
};

export default Speed;
